<template>
  <main-template>
    <v-row class="mb-1">
      <v-col>
        <h1>
          <v-btn color="primary" x-small text @click="$router.go(-1)">
            <v-icon left>fa fa-angle-left</v-icon>
          </v-btn>
          Justificar Hora Extra
        </h1>
      </v-col>
    </v-row>

    <v-alert type="info" text dense class="mb-4">
      Estas são as suas horas extras que aguardam a aprovação do gestor. Explique o motivo de cada
      uma (e anexe um documento, se tiver): o gestor vê a justificativa na hora de aprovar. A
      justificativa é opcional e pode ser editada enquanto a hora extra estiver pendente.
    </v-alert>

    <v-card outlined class="rounded-lg shadow-sm">
      <v-card-title class="py-3 px-4 d-flex align-center justify-space-between">
        <span class="subtitle-1 font-weight-bold">Horas extras aguardando aprovação</span>
        <v-btn icon color="primary" :loading="loading" @click="carregar">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-data-table
        :headers="headers"
        :items="horasExtras"
        :loading="loading"
        loading-text="Carregando horas extras..."
        class="elevation-0"
      >
        <template v-slot:item.data="{ item }">
          <span class="font-weight-medium">{{ formatarData(item.data) }}</span>
        </template>

        <template v-slot:item.minutos_previstos="{ item }">
          {{ formatarMinutos(item.minutos_previstos) }}
        </template>

        <template v-slot:item.minutos_realizados="{ item }">
          {{ formatarMinutos(item.minutos_realizados) }}
        </template>

        <template v-slot:item.minutos_extras="{ item }">
          <v-chip
            small
            color="orange lighten-5"
            text-color="orange darken-3"
            class="font-weight-bold"
          >
            +{{ formatarMinutos(item.minutos_extras) }}
          </v-chip>
        </template>

        <template v-slot:item.justificativa="{ item }">
          <div v-if="item.justificativa">
            <span
              class="text-truncate d-inline-block"
              style="max-width: 260px"
              :title="item.justificativa.motivo"
            >
              {{ item.justificativa.motivo }}
            </span>
            <div v-if="item.justificativa.tem_anexo">
              <v-btn color="info" text x-small class="px-0" @click="abrirAnexo(item)">
                <v-icon left x-small>mdi-paperclip</v-icon> Ver anexo
              </v-btn>
            </div>
          </div>
          <v-chip v-else x-small color="amber lighten-4" text-color="amber darken-4">
            Não justificada
          </v-chip>
        </template>

        <template v-slot:item.acoes="{ item }">
          <div class="d-flex justify-end">
            <v-btn small color="primary" class="rounded-lg" @click="abrirFormulario(item)">
              <v-icon left small>{{
                item.justificativa ? "mdi-pencil" : "mdi-text-box-plus-outline"
              }}</v-icon>
              {{ item.justificativa ? "Editar" : "Justificar" }}
            </v-btn>
            <v-tooltip v-if="item.justificativa" bottom>
              <template #activator="{ on }">
                <v-btn
                  icon
                  small
                  color="error"
                  class="ml-1"
                  v-on="on"
                  @click="confirmarExclusao(item)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Excluir justificativa</span>
            </v-tooltip>
          </div>
        </template>

        <template v-slot:no-data>
          <div class="pa-6 text-center grey--text">
            <v-icon size="48" color="green lighten-2">mdi-check-all</v-icon>
            <div class="mt-2 text-subtitle-1">Você não tem horas extras aguardando aprovação.</div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- FORMULÁRIO -->
    <v-dialog v-model="dialogForm" max-width="560" persistent>
      <v-card rounded="xl">
        <v-card-title class="px-6 pt-6 pb-2 font-weight-bold">
          <v-icon left color="primary">mdi-clock-plus-outline</v-icon>
          Justificar hora extra
        </v-card-title>
        <v-card-text class="px-6 py-2" v-if="itemSelecionado">
          <p class="mb-4">
            Dia <strong>{{ formatarData(itemSelecionado.data) }}</strong
            >: <strong>+{{ formatarMinutos(itemSelecionado.minutos_extras) }}</strong> de hora
            extra.
          </p>
          <v-form ref="form" v-model="formValido" @submit.prevent="salvar">
            <v-textarea
              v-model="form.motivo"
              label="Motivo da hora extra *"
              placeholder="Ex.: fechamento do inventário, cobertura de colega, entrega urgente..."
              outlined
              rows="4"
              counter="2000"
              :rules="regrasMotivo"
            />
            <v-file-input
              v-model="form.anexo"
              :label="
                temAnexoAtual
                  ? 'Trocar anexo (PDF, JPEG, PNG - máx. 10MB)'
                  : 'Anexar documento (PDF, JPEG, PNG - máx. 10MB)'
              "
              outlined
              dense
              show-size
              accept=".pdf,.jpg,.jpeg,.png"
              prepend-icon=""
              prepend-inner-icon="mdi-paperclip"
              clearable
              :rules="[validarArquivo]"
            />
            <v-checkbox
              v-if="temAnexoAtual && !form.anexo"
              v-model="form.removerAnexo"
              label="Remover o anexo enviado"
              dense
              hide-details
              class="mt-0"
            />
          </v-form>
        </v-card-text>
        <v-card-actions class="px-6 pb-6 pt-2 d-flex justify-end">
          <v-btn text :disabled="salvando" @click="dialogForm = false">Cancelar</v-btn>
          <v-btn
            color="primary"
            class="rounded-lg font-weight-bold"
            :loading="salvando"
            :disabled="!formValido"
            @click="salvar"
          >
            Salvar justificativa
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- EXCLUIR -->
    <v-dialog v-model="dialogExcluir" max-width="440">
      <v-card rounded="xl">
        <v-card-title class="px-6 pt-6 pb-2 font-weight-bold">Excluir justificativa</v-card-title>
        <v-card-text class="px-6 py-2" v-if="itemParaExcluir">
          Excluir a justificativa da hora extra do dia
          <strong>{{ formatarData(itemParaExcluir.data) }}</strong
          >? A hora extra continua aguardando aprovação, só sem o motivo.
        </v-card-text>
        <v-card-actions class="px-6 pb-6 pt-2 d-flex justify-end">
          <v-btn text :disabled="excluindo" @click="dialogExcluir = false">Cancelar</v-btn>
          <v-btn
            color="error"
            class="rounded-lg font-weight-bold"
            :loading="excluindo"
            @click="excluir"
          >
            Excluir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </main-template>
</template>

<script>
export default {
  name: "JustificarHoraExtraPage",
  data() {
    return {
      loading: false,
      horasExtras: [],
      dialogForm: false,
      formValido: false,
      itemSelecionado: null,
      form: { motivo: "", anexo: null, removerAnexo: false },
      salvando: false,
      dialogExcluir: false,
      itemParaExcluir: null,
      excluindo: false,
      regrasMotivo: [
        (v) => !!(v && v.trim()) || "Informe o motivo da hora extra",
        (v) => (v || "").trim().length >= 10 || "Escreva pelo menos 10 caracteres",
        (v) => (v || "").length <= 2000 || "Máximo de 2000 caracteres",
      ],
      headers: [
        { text: "Data", value: "data", sortable: false },
        { text: "Previsto", value: "minutos_previstos", sortable: false },
        { text: "Realizado", value: "minutos_realizados", sortable: false },
        { text: "Hora extra", value: "minutos_extras", sortable: false },
        { text: "Justificativa", value: "justificativa", sortable: false },
        { text: "", value: "acoes", sortable: false, align: "end" },
      ],
    };
  },
  computed: {
    temAnexoAtual() {
      return !!this.itemSelecionado?.justificativa?.tem_anexo;
    },
  },
  mounted() {
    this.carregar();
  },
  methods: {
    async carregar() {
      try {
        this.loading = true;
        const res = await this.$services.bancoHorasService.getHorasExtrasParaJustificar();
        this.horasExtras = res?.horas_extras || [];
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.loading = false;
      }
    },
    abrirFormulario(item) {
      this.itemSelecionado = item;
      this.form = {
        motivo: item.justificativa?.motivo || "",
        anexo: null,
        removerAnexo: false,
      };
      this.dialogForm = true;
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },
    async salvar() {
      if (!this.$refs.form.validate()) return;

      try {
        this.salvando = true;
        const formData = new FormData();
        formData.append("motivo", this.form.motivo.trim());
        if (this.form.anexo) {
          formData.append("anexo", this.form.anexo);
        } else if (this.form.removerAnexo) {
          formData.append("remover_anexo", "1");
        }

        await this.$services.bancoHorasService.salvarJustificativaHoraExtra(
          this.itemSelecionado.id,
          formData
        );
        this.$toast.success("Justificativa salva! O gestor verá o motivo ao aprovar.");
        this.dialogForm = false;
        await this.carregar();
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.salvando = false;
      }
    },
    confirmarExclusao(item) {
      this.itemParaExcluir = item;
      this.dialogExcluir = true;
    },
    async excluir() {
      try {
        this.excluindo = true;
        await this.$services.bancoHorasService.excluirJustificativaHoraExtra(
          this.itemParaExcluir.id
        );
        this.$toast.success("Justificativa excluída.");
        this.dialogExcluir = false;
        await this.carregar();
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.excluindo = false;
      }
    },
    async abrirAnexo(item) {
      try {
        await this.$services.bancoHorasService.abrirMeuAnexoJustificativa(item.id);
      } catch (error) {
        this.$handleError(error);
      }
    },
    validarArquivo(file) {
      if (!file) return true;
      const tiposPermitidos = ["application/pdf", "image/jpeg", "image/png"];
      const extensoesPermitidas = [".pdf", ".jpg", ".jpeg", ".png"];
      const ext = file.name ? `.${file.name.split(".").pop().toLowerCase()}` : "";
      if (!tiposPermitidos.includes(file.type) && !extensoesPermitidas.includes(ext)) {
        return "Apenas PDF, JPG ou PNG são permitidos";
      }
      if (file.size > 10 * 1024 * 1024) return "O arquivo excede o limite máximo de 10 MB";
      return true;
    },
    formatarData(dataStr) {
      if (!dataStr) return "-";
      const partes = String(dataStr).split("T")[0].split("-");
      return partes.length === 3 ? `${partes[2]}/${partes[1]}/${partes[0]}` : dataStr;
    },
    formatarMinutos(minutos) {
      const abs = Math.abs(minutos || 0);
      return `${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(
        2,
        "0"
      )}`;
    },
  },
};
</script>
